import { Collection } from 'firestorter';

import Collections from '../utils/collections';
import { firestorePaths } from '../utils/paths';
import BaseDocument from './BaseDocument';

export const LocationType = {
  HOME: 'HOME',
  GYM: 'GYM',
};

export const TrackType = {
  STANDARD: 'STANDARD',
  HYROX: 'HYROX',
};

/**
 * Class representing a track.
 *
 * @class Track
 * @extends BaseDocument
 */
class Track extends BaseDocument {
  /**
   * Creates an instance of a track.
   * @param {string} id - The ID of the track document.
   * @param {Object} opts - Optional parameters for initializing the document.
   */
  constructor(id, opts) {
    super(`${Collections.TRACK}/${id}`, opts);
  }

  get name() {
    return this.data.name;
  }

  get programs() {
    return this.data.programs || [];
  }

  get createdAt() {
    return this.data.createdAt;
  }

  get equipment() {
    return this.data.equipment || [];
  }

  get difficulty() {
    return this.data.difficulty;
  }

  get location() {
    return this.data.location;
  }

  // Tracks created before track types existed have no type — treat as STANDARD.
  get type() {
    return this.data.type || TrackType.STANDARD;
  }

  get isHyroxTrack() {
    return this.type === TrackType.HYROX;
  }

  get isArchived() {
    return !!this.data.isArchived;
  }

  get coach() {
    return this.data.coach;
  }

  get isPublic() {
    return !!this.data.isPublic;
  }

  /**
   * How many workouts a week of this track holds. The assignment pipeline only
   * honours a client's preferred workout days when this matches how many days
   * they picked.
   * @return {number|void}
   */
  get workoutsPerWeek() {
    return this.data.workoutsPerWeek;
  }

  async archive() {
    return this.updateFields({
      isArchived: true,
      archivedAt: new Date(),
    });
  }

  async unarchive() {
    return this.updateFields({
      isArchived: false,
    });
  }

  static async getTrackById(trackId) {
    const trackDoc = new Track(trackId);
    await trackDoc.init();
    return trackDoc.exists ? trackDoc : null;
  }

  static async getTracksByCoach(coachId, fetchArchivedTracks) {
    return Track.getTracksByCoachIds([coachId], fetchArchivedTracks);
  }

  static async getTracksByCoachIds(coachIds = [], fetchArchivedTracks = false) {
    const normalizedIds = coachIds.filter(Boolean);
    const trackCollection = new Collection(firestorePaths.TRACK, {
      query: (ref) => {
        let query = ref.where('coach', '==', '__missing__');
        if (normalizedIds.length === 1) {
          query = ref.where('coach', '==', normalizedIds[0]);
        } else if (normalizedIds.length > 1) {
          query = ref.where('coach', 'in', normalizedIds);
        }
        if (!fetchArchivedTracks) {
          query = query.where('isArchived', '==', false);
        }
        return query;
      },
      createDocument: ({ id }, options) => new Track(id, options),
    });
    await Track.initCollection(trackCollection);
    return trackCollection;
  }

  /**
   * Get the public tracks.
   * @return {Promise<Collection>} The collection of public tracks.
   */
  static async getPublicTracks(fetchArchivedTracks = false) {
    const trackCollection = new Collection(firestorePaths.TRACK, {
      query: (ref) => {
        let query = ref.where('isPublic', '==', true);
        if (!fetchArchivedTracks) {
          query = query.where('isArchived', '==', false);
        }
        return query;
      },
      createDocument: ({ id }, options) => new Track(id, options),
    });
    await Track.initCollection(trackCollection);
    return trackCollection;
  }

  static async addDoc(data) {
    const trackCollection = new Collection(Collections.TRACK);
    const doc = await trackCollection.add(data);
    const trackDoc = new Track(doc.id);
    await trackDoc.init();
    return trackDoc;
  }
}

export default Track;
